import type { AppRoute } from 'src/routes/types';

import { Book, Comment, LockClock } from '@mui/icons-material';
import { t } from 'i18next';
import { lazy } from 'react';
import { Icon } from 'src/shared/components/Icon/Icon.tsx';

const PayrollScheduleListView = lazy(
  () => import('./views/PayrollScheduleListView.tsx')
);
const PlacementNoteListView = lazy(
  () => import('./views/PlacementNoteListView.tsx')
);
const CompanyUserNoteListView = lazy(
  () => import('./views/CompanyUserNoteListView.tsx')
);
const CompanyNoteListView = lazy(
  () => import('./views/CompanyNoteListView.tsx')
);
const CurrentUserNoteListView = lazy(
  () => import('./views/CurrentUserNoteListView.tsx')
);
const BasisView = lazy(() => import('./views/BasisView.tsx'));

const noteRoutePaths = {
  companyNoteListView: '/accounting/company-notes',
  companyUserNoteList: '/users/:id/accounting/notes',
  currentUserNoteListView: '/accounting/my-notes',
  placementNoteListView: '/placements/:id/accounting/notes',
} as const;

const noteRoutes: AppRoute[] = [
  {
    element: <CompanyNoteListView />,
    enabled: true,
    menu: {
      group: 'management',
      icon: <Comment />,
      title: () => t('accounting:COMPANY_NOTES'),
    },
    order: 0,
    path: noteRoutePaths.companyNoteListView,
    permission: 'admin',
    scope: 'private',
  },
  {
    element: <CurrentUserNoteListView />,
    enabled: true,
    menu: {
      group: 'management',
      icon: <Icon icon="eva:book-open-outline" />,
      title: () => t('accounting:CURRENT_USER_NOTES'),
    },
    order: 0,
    path: noteRoutePaths.currentUserNoteListView,
    permission: 'all',
    scope: 'private',
  },
  {
    _dangerouslyInjectAsChildForLegacyPath: 'placements/:id',
    element: <PlacementNoteListView />,
    enabled: true,
    path: noteRoutePaths.placementNoteListView,
    permission: 'all',
    scope: 'private',
  },
  {
    _dangerouslyInjectAsChildForLegacyPath: 'users/:id',
    element: <CompanyUserNoteListView />,
    enabled: true,
    path: noteRoutePaths.companyUserNoteList,
    permission: 'admin',
    scope: 'private',
  },
];

const payrollScheduleRoutePaths = {
  payrollScheduleListView: '/accounting/payroll-schedules',
} as const;

const payrollScheduleRoutes: AppRoute[] = [
  {
    element: <PayrollScheduleListView />,
    enabled: true,
    menu: {
      group: 'management',
      icon: <LockClock />,
      title: () => t('accounting:PAYROLL_SCHEDULES'),
    },
    order: 2,
    path: payrollScheduleRoutePaths.payrollScheduleListView,
    permission: 'admin',
    scope: 'private',
  },
];

const basisRoutePaths = {
  basisView: '/accounting/basis',
} as const;

const basisRoute: AppRoute = {
  element: <BasisView />,
  enabled: true,
  menu: {
    group: 'management',
    icon: <Book />,
    title: () => t('accounting:BASIS'),
  },
  order: 0,
  path: basisRoutePaths.basisView,
  permission: 'admin',
  scope: 'private',
};

export const accountingRoutePaths = {
  ...basisRoutePaths,
  ...noteRoutePaths,
  ...payrollScheduleRoutePaths,
} as const;

export const accountingRoutes = [
  ...payrollScheduleRoutes,
  ...noteRoutes,
  basisRoute,
];
